import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useT } from "@/lib/i18n";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_europe-trade-hub/artifacts/wkf5kbul_Zopran%20Logo%20Rundausschnitt%20%281%29.png";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const { t } = useT();

  const NAV = [
    { to: "/", label: t("nav.home") },
    { to: "/services", label: t("nav.services") },
    { to: "/industries", label: t("nav.industries") },
    { to: "/about", label: t("nav.about") },
    { to: "/partner", label: t("nav.partner") },
    { to: "/contact", label: t("nav.contact") },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-white/85 border-b border-[color:var(--zp-border)] shadow-[0_4px_24px_-12px_rgba(10,37,64,0.18)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 h-20 flex items-center justify-between">
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-3 group">
          <div className="w-11 h-11 rounded-full overflow-hidden ring-1 ring-[color:var(--zp-cyan-2)]/40 bg-white shadow-[0_0_22px_rgba(92,225,230,0.35)]">
            <img src={LOGO_URL} alt="Zopran" className="w-full h-full object-cover" />
          </div>
          <div className="leading-tight">
            <div className="font-display font-bold tracking-tight text-[color:var(--zp-navy)] text-[15px]">
              ZOPRAN
            </div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--zp-cyan-3)] -mt-0.5 font-semibold">
              Global Logistics
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-link-${n.to.replace("/", "") || "home"}`}
              className={({ isActive }) =>
                `px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  isActive
                    ? "text-[color:var(--zp-navy)] bg-[color:var(--zp-bg-2)] border border-[color:var(--zp-border)]"
                    : "text-[color:var(--zp-text-2)] hover:text-[color:var(--zp-navy)]"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          <LanguageSwitcher />
          <button
            data-testid="navbar-quote-btn"
            onClick={() => navigate("/quote")}
            className="zp-btn-primary text-sm"
          >
            {t("nav.quote")}
            <ArrowUpRight size={16} />
          </button>
        </div>

        <div className="lg:hidden flex items-center gap-2">
          <LanguageSwitcher compact />
          <button
            data-testid="navbar-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="w-10 h-10 grid place-items-center rounded-full border border-[color:var(--zp-border)] bg-white text-[color:var(--zp-navy)]"
            aria-label="Toggle menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[color:var(--zp-border)] bg-white/95 backdrop-blur-xl">
          <div className="px-6 py-5 flex flex-col gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${n.to.replace("/", "") || "home"}`}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-xl text-sm font-medium ${
                    isActive
                      ? "bg-[color:var(--zp-bg-2)] text-[color:var(--zp-navy)]"
                      : "text-[color:var(--zp-text-2)]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <button
              data-testid="mobile-quote-btn"
              onClick={() => {
                setOpen(false);
                navigate("/quote");
              }}
              className="zp-btn-primary w-full mt-3 text-sm"
            >
              {t("nav.quote")}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
